import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Loader2, Shield, Sparkles } from 'lucide-react';

const PLAN_LABELS = {
  free: { name: 'Free', color: 'text-neutral-400', bg: 'bg-neutral-800' },
  pro: { name: 'Pro', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  ultra: { name: 'Ultra', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  enterprise: { name: 'Enterprise', color: 'text-amber-400', bg: 'bg-amber-500/10' },
};

export default function Account() {
  const { user } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      if (!user) return;
      const snap = await getDoc(doc(firestore, 'users', user.uid));
      if (snap.exists()) {
        setUserData(snap.data());
      } else {
        const defaults = {
          email: user.email,
          displayName: user.displayName || '',
          plan: 'free',
          createdAt: new Date().toISOString(),
        };
        await setDoc(doc(firestore, 'users', user.uid), defaults, { merge: true });
        setUserData(defaults);
      }
      setLoading(false);
    };
    fetchUser();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <p className="text-neutral-400 mb-4">Please sign in to view your account.</p>
          <Link to="/" className="text-sm text-white underline underline-offset-4" data-testid="account-back-link">Go back</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-500" />
      </div>
    );
  }

  const plan = userData?.plan || 'free';
  const planInfo = PLAN_LABELS[plan] || PLAN_LABELS.free;

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="account-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-white transition-colors mb-8" data-testid="account-back">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
        </Link>

        <h1 className="font-heading text-3xl font-medium tracking-tight text-white mb-8" data-testid="account-title">Account</h1>

        <div className="space-y-6">
          {/* Profile card */}
          <div className="rounded-2xl border border-white/[0.08] bg-surface p-8" data-testid="account-profile-card">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-xl font-heading font-medium text-white">
                {(userData?.displayName?.[0] || user.email?.[0])?.toUpperCase()}
              </div>
              <div>
                <h3 className="font-heading text-lg font-medium text-white">{userData?.displayName || 'User'}</h3>
                <p className="text-sm text-neutral-500">{user.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-neutral-500" strokeWidth={1.5} />
              <span className="text-sm text-neutral-400">Member since {new Date(userData?.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
            </div>
          </div>

          {/* Plan card */}
          <div className="rounded-2xl border border-white/[0.08] bg-surface p-8" data-testid="account-plan-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-medium text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                Current Plan
              </h3>
              <span className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${planInfo.bg} ${planInfo.color}`} data-testid="account-plan-badge">
                {planInfo.name}
              </span>
            </div>
            <p className="text-sm text-neutral-500 mb-6">
              {plan === 'free'
                ? 'You are on the free open-source plan. Upgrade for extended capabilities.'
                : `You are on the ${planInfo.name} plan.`}
            </p>
            {plan === 'free' && (
              <Link
                to="/#pricing"
                className="inline-flex items-center gap-2 rounded-md bg-white text-black px-5 py-2.5 text-sm font-medium hover:bg-neutral-200 transition-colors"
                data-testid="account-upgrade-button"
              >
                <CreditCard className="w-4 h-4" strokeWidth={1.5} />
                Upgrade Plan
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
